import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

export default function CreateListing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [cats, setCats] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [deliveryOpts, setDeliveryOpts] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", categories: [], customCategory: "", condition: "", listing_type: "rent", price_per_day: "", sale_price: "", requires_deposit: false, item_value: "", deposit_amount: "", location: "", delivery_options: [] });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/config").then((r) => { setConditions(r.data.conditions || []); setDeliveryOpts(r.data.delivery_options || []); });
  }, []);

  useEffect(() => {
    if (!editing || !cats.length) return;
    api.get(`/listings/${id}`).then((r) => {
      const l = r.data;
      const listCats = (l.categories && l.categories.length) ? l.categories : (l.category ? [l.category] : []);
      const known = listCats.filter((c) => cats.includes(c));
      const customs = listCats.filter((c) => !cats.includes(c));
      const selected = [...known];
      if (customs.length && !selected.includes("Other")) selected.push("Other");
      setForm({
        title: l.title || "", description: l.description || "",
        categories: selected, customCategory: customs.join(", "),
        condition: l.condition || "", listing_type: l.listing_type || "rent",
        price_per_day: l.price_per_day ?? "", sale_price: l.sale_price ?? "",
        requires_deposit: !!l.requires_deposit, item_value: l.item_value ?? "",
        deposit_amount: l.deposit_amount ?? "", location: l.location || "",
        delivery_options: l.delivery_options || [],
      });
      setImages(l.images || []);
    }).catch(() => toast.error("Could not load listing"));
  }, [editing, id, cats]);

  const toggleCategory = (c) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
  }));

  const toggleDelivery = (o) => setForm((f) => ({
    ...f,
    delivery_options: f.delivery_options.includes(o) ? f.delivery_options.filter((x) => x !== o) : [...f.delivery_options, o],
  }));

  const buildCategories = () => {
    const base = form.categories.filter((c) => c !== "Other");
    if (form.categories.includes("Other") && form.customCategory.trim()) {
      form.customCategory.split(",").map((s) => s.trim()).filter(Boolean).forEach((c) => base.push(c));
    }
    return base;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, res.data.path]);
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const finalCats = buildCategories();
    if (!form.title || !finalCats.length || !form.location) { toast.error("Add a title, at least one category and a location"); return; }
    if (form.categories.includes("Other") && !form.customCategory.trim()) { toast.error("Type your custom category (e.g. Books, Home)"); return; }
    const needsRent = form.listing_type === "rent" || form.listing_type === "both";
    const needsSale = form.listing_type === "sale" || form.listing_type === "both";
    if (needsRent && !form.price_per_day) { toast.error("Enter a rental price / day"); return; }
    if (needsSale && !form.sale_price) { toast.error("Enter a sale price"); return; }
    const wantsDeposit = needsRent && form.requires_deposit;
    if (wantsDeposit && !form.deposit_amount) { toast.error("Enter a deposit amount"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description,
        categories: finalCats, category: finalCats[0],
        condition: form.condition || null,
        location: form.location, listing_type: form.listing_type,
        delivery_options: form.delivery_options,
        price_per_day: needsRent ? parseFloat(form.price_per_day) : null,
        sale_price: needsSale ? parseFloat(form.sale_price) : null,
        requires_deposit: wantsDeposit,
        item_value: wantsDeposit && form.item_value ? parseFloat(form.item_value) : null,
        deposit_amount: wantsDeposit ? parseFloat(form.deposit_amount) : null,
        images,
      };
      const res = editing
        ? await api.put(`/listings/${id}`, payload)
        : await api.post("/listings", payload);
      toast.success(editing ? "Listing updated!" : "Listing published!");
      navigate(`/listing/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${editing ? "update" : "create"} listing`); }
    finally { setSaving(false); }
  };

  const TYPES = [
    { key: "rent", label: "For Rent" },
    { key: "sale", label: "For Sale" },
    { key: "both", label: "Rent & Sell" },
  ];
  const showRent = form.listing_type === "rent" || form.listing_type === "both";
  const showSale = form.listing_type === "sale" || form.listing_type === "both";

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-display text-4xl font-bold tracking-tight">{editing ? "Edit your item" : "List your item"}</h1>
      <p className="text-muted-foreground mt-1">{editing ? "Update the details, price or photos of your listing." : "Earn money renting out gear you're not using."}</p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Photos</label>
          <div className="mt-2 flex flex-wrap gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-black/10">
                <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/70 rounded-full p-1">
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            <label data-testid="upload-label" className="w-24 h-24 rounded-xl border-2 border-dashed border-black/20 flex flex-col items-center justify-center cursor-pointer hover:border-black transition-colors">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5 text-muted-foreground" /><span className="text-[10px] mt-1 text-muted-foreground">Add</span></>}
              <input data-testid="image-input" type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
            </label>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Title *</label>
          <Input data-testid="title-input" value={form.title} onChange={set("title")} placeholder="e.g. DeWalt 20V Cordless Drill" className="mt-2 h-12 bg-white border-black/10 rounded-xl" />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Description</label>
          <Textarea data-testid="description-input" value={form.description} onChange={set("description")} placeholder="Condition, what's included, pickup notes…" className="mt-2 bg-white border-black/10 rounded-xl min-h-28" />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Listing type *</label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`type-${t.key}`}
                onClick={() => setForm({ ...form, listing_type: t.key })}
                className={`h-12 rounded-xl border text-sm font-medium transition-colors ${
                  form.listing_type === t.key ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Categories * <span className="normal-case font-normal text-muted-foreground/70">(pick one or more)</span></label>
          <div data-testid="category-chips" className="mt-2 flex flex-wrap gap-2">
            {cats.map((c) => {
              const active = form.categories.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`category-chip-${c}`}
                  onClick={() => toggleCategory(c)}
                  className={`px-3.5 py-2 rounded-full border text-sm font-medium transition-colors ${active ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black"}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          {form.categories.includes("Other") && (
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Your category *</label>
              <Input
                data-testid="custom-category-input"
                value={form.customCategory}
                onChange={set("customCategory")}
                placeholder="e.g. Books, Home, Kitchen"
                className="mt-2 h-12 bg-white border-black/10 rounded-xl"
              />
              <p className="text-xs text-muted-foreground mt-1.5">Separate multiple with commas.</p>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Condition</label>
          <Select value={form.condition} onValueChange={set("condition")}>
            <SelectTrigger data-testid="condition-select" className="mt-2 h-12 bg-white border-black/10 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{conditions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Delivery options <span className="normal-case font-normal text-muted-foreground/70">(how renters get the item)</span></label>
          <div data-testid="delivery-chips" className="mt-2 flex flex-wrap gap-2">
            {deliveryOpts.map((o) => {
              const active = form.delivery_options.includes(o);
              return (
                <button
                  key={o}
                  type="button"
                  data-testid={`delivery-chip-${o}`}
                  onClick={() => toggleDelivery(o)}
                  className={`px-3.5 py-2 rounded-full border text-sm font-medium transition-colors ${active ? "bg-[#FF5A00] text-white border-[#FF5A00]" : "bg-white border-black/15 hover:border-black"}`}
                >
                  {o}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {showRent && (
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Rental / day (€) *</label>
              <Input data-testid="price-input" type="number" min="1" step="0.01" value={form.price_per_day} onChange={set("price_per_day")} placeholder="25" className="mt-2 h-12 bg-white border-black/10 rounded-xl" />
            </div>
          )}
          {showSale && (
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Sale price (€) *</label>
              <Input data-testid="sale-price-input" type="number" min="1" step="0.01" value={form.sale_price} onChange={set("sale_price")} placeholder="180" className="mt-2 h-12 bg-white border-black/10 rounded-xl" />
            </div>
          )}
        </div>

        {showRent && (
          <div className="bg-[#F4F1EC] border border-black/10 rounded-2xl p-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span>
                <span className="font-semibold block flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#FF5A00]" /> Require a security deposit</span>
                <span className="text-sm text-muted-foreground">Protects against damage, theft or non-return. Refunded when you mark the item returned.</span>
              </span>
              <input
                data-testid="deposit-toggle"
                type="checkbox"
                checked={form.requires_deposit}
                onChange={(e) => setForm({ ...form, requires_deposit: e.target.checked })}
                className="w-5 h-5 accent-[#FF5A00] shrink-0 ml-3"
              />
            </label>

            {form.requires_deposit && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Estimated item value (€)</label>
                  <Input data-testid="item-value-input" type="number" min="1" step="0.01" value={form.item_value} onChange={set("item_value")} placeholder="e.g. 300" className="mt-2 h-11 bg-white border-black/10 rounded-xl" />
                  <p className="text-xs text-muted-foreground mt-1.5">Tip: keep the deposit reasonable — enough to cover loss, low enough to attract renters. We suggest 10–30% of item value.</p>
                </div>

                {form.item_value > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {[10, 20, 30, 50].map((p) => {
                      const val = Math.round(parseFloat(form.item_value) * (p / 100) * 100) / 100;
                      return (
                        <button
                          key={p}
                          type="button"
                          data-testid={`deposit-suggest-${p}`}
                          onClick={() => setForm({ ...form, deposit_amount: String(val) })}
                          className="px-3.5 py-2 rounded-full border border-black/15 bg-white text-sm font-medium hover:border-black transition-colors"
                        >
                          {p}% · €{val}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Deposit amount (€) *</label>
                  <Input data-testid="deposit-amount-input" type="number" min="1" step="0.01" value={form.deposit_amount} onChange={set("deposit_amount")} placeholder="60" className="mt-2 h-11 bg-white border-black/10 rounded-xl" />
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Location *</label>
          <Input data-testid="location-input" value={form.location} onChange={set("location")} placeholder="e.g. Brooklyn, NY" className="mt-2 h-12 bg-white border-black/10 rounded-xl" />
        </div>

        <button data-testid="publish-btn" disabled={saving} className="w-full bg-[#0A0A0A] text-white rounded-full py-3.5 font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60">
          {saving ? (editing ? "Saving…" : "Publishing…") : (editing ? "Save changes" : "Publish listing")}
        </button>
      </form>
    </div>
  );
}
